import { Footer } from '@/components/Footer';
import { Header } from '@/components/Header';
import { SortOption } from '@/components/search/searchUtils';
import { toast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  capitalizeFirstLetter,
  DAYS_ORDER,
  formatTime,
} from '@/utils/dateTime';
import {
  Clock,
  Mail,
  MapPin,
  MessageSquare,
  Navigation,
  Phone,
  Send,
} from 'lucide-react';
import { useState } from 'react';

const workHoursMap = new Map<string, { open: string; close: string }[]>();

export default function ContactPage() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('name-asc');
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    inquiryType: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    toast.success(
      "Your message has been sent! We'll get back to you within 24 hours."
    );
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
      inquiryType: '',
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header
        showSearchInHeader={false}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        sortBy={sortBy}
        setSortBy={setSortBy}
        filterMenuOpen={filterMenuOpen}
        setFilterMenuOpen={setFilterMenuOpen}
        sortMenuOpen={sortMenuOpen}
        setSortMenuOpen={setSortMenuOpen}
      />

      <main className="flex-1 bg-white">
        <div className="container mx-auto py-8">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl mb-4 text-[var(--dispensary-gray-700)]">
              Contact Us
            </h1>
            <p className="text-xl text-[var(--dispensary-gray-500)] max-w-2xl mx-auto">
              We're here to help with any questions about medications,
              prescriptions, or our services.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Send us a Message
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          required
                          value={formData.name}
                          onChange={e =>
                            handleInputChange('name', e.target.value)
                          }
                          placeholder="Enter your full name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={e =>
                            handleInputChange('phone', e.target.value)
                          }
                          placeholder="+234 901 234 5678"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={e =>
                          handleInputChange('email', e.target.value)
                        }
                        placeholder="your.email@example.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="inquiryType">Type of Inquiry</Label>
                      <Select
                        value={formData.inquiryType}
                        onValueChange={value =>
                          handleInputChange('inquiryType', value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select inquiry type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">
                            General Question
                          </SelectItem>
                          <SelectItem value="prescription">
                            Prescription Inquiry
                          </SelectItem>
                          <SelectItem value="order">Order Status</SelectItem>
                          <SelectItem value="delivery">
                            Delivery Question
                          </SelectItem>
                          <SelectItem value="medication">
                            Medication Information
                          </SelectItem>
                          <SelectItem value="consultation">
                            Pharmacist Consultation
                          </SelectItem>
                          <SelectItem value="complaint">
                            Complaint or Feedback
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="subject">Subject *</Label>
                      <Input
                        id="subject"
                        required
                        value={formData.subject}
                        onChange={e =>
                          handleInputChange('subject', e.target.value)
                        }
                        placeholder="Brief description of your inquiry"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        required
                        value={formData.message}
                        onChange={e =>
                          handleInputChange('message', e.target.value)
                        }
                        placeholder="Please provide details about your inquiry..."
                        className="min-h-[120px]"
                      />
                    </div>

                    <Button type="submit" className="w-full">
                      <Send className="h-4 w-4 mr-2" />
                      Send Message
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-[var(--primary)] mt-0.5" />
                    Get in Touch
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Phone className="h-5 w-5 text-[var(--primary)] mt-0.5" />
                      <div>
                        <p className="font-medium">Phone</p>
                        <p className="bodysmall text-[var(--dispensary-gray-500)]">
                          +234 901 234 5678
                        </p>
                        <p className="captionmedium text-[var(--dispensary-gray-400)]">
                          Mon-Fri: 8AM-8PM
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Mail className="h-5 w-5 text-[var(--primary)] mt-0.5" />
                      <div>
                        <p className="font-medium">Email</p>
                        <p className="bodysmall text-[var(--dispensary-gray-500)]">
                          info@pharmacy.com
                        </p>
                        <p className="captionmedium text-[var(--dispensary-gray-400)]">
                          Response within 24 hours
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <MessageSquare className="h-5 w-5 text-[var(--primary)] mt-0.5" />
                      <div>
                        <p className="font-medium">WhatsApp</p>
                        <p className="bodysmall text-[var(--dispensary-gray-500)]">
                          +234 901 234 5678
                        </p>
                        <p className="captionmedium text-[var(--dispensary-gray-400)]">
                          Quick responses
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <Button variant="outline" className="w-full">
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Start WhatsApp Chat
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Visit Our Store
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="mb-2">Address</h4>
                    <p className="bodysmall text-[var(--dispensary-gray-500)]">
                      123 Health Street
                      <br />
                      Victoria Island
                      <br />
                      Lagos, Nigeria
                    </p>
                  </div>

                  <div>
                    <h4 className="mb-2 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Operating Hours
                    </h4>
                    <div className="bodysmall text-[var(--dispensary-gray-600)] space-y-1">
                      {DAYS_ORDER.map(day => {
                        const hours = workHoursMap.get(day);
                        if (!hours) {
                          return (
                            <div key={day} className="flex justify-between">
                              <span>{capitalizeFirstLetter(day)}</span>
                              <span>Closed</span>
                            </div>
                          );
                        }
                        return (
                          <div key={day} className="flex justify-between">
                            <span>{capitalizeFirstLetter(day)}</span>
                            <span>
                              {hours.map((h, idx) => (
                                <span key={idx}>
                                  {formatTime(h.open)} - {formatTime(h.close)}
                                  {idx < hours.length - 1 && ', '}
                                </span>
                              ))}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <Button className="w-full">
                    <Navigation className="h-4 w-4 mr-2" />
                    Get Directions
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Emergency</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="bodysmall text-[var(--dispensary-gray-500)] mb-3">
                    For medical emergencies, please call 199 or visit the
                    nearest hospital.
                  </p>
                  <p className="bodysmall text-[var(--dispensary-gray-500)]">
                    For urgent medication questions outside business hours, you
                    can reach our on-call pharmacist.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
